import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './Pages/Home';
import DomainPage from './Pages/DomainPage';
import FAQPage from './Pages/FAQPage';
import ContactPage from './Pages/ContactPage';
import PersonalCabinetPage from './Pages/PersonalCabinetPage';
import AdminPanelPage from './Pages/AdminPanelPage';
import PrivacyPolicyPage from './Pages/PrivacyPolicyPage';
import TermsOfServicePage from './Pages/TermsOfServicePage';
import Navbar from './Components/Navbar';
import NotFoundPage from './Pages/NotFoundPage';
import DomainSearchPage from './Pages/DomainSearchPage';
import MiniAppTabBar from './Components/MiniAppTabBar';
import { isTelegramMiniApp, loadTelegramWebAppScript } from './Utils/miniApp';
import { applyThemeColor } from './Utils/theme';
import { getAuthConfig } from './Utils/authConfig';

// Initialize base theme color immediately on evaluation
applyThemeColor();

// ScrollToTop component to reset scroll position on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Always scroll to top when pathname changes
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Direct pass-through for server-rendered endpoints (e.g. sitemap.xml, robots.txt)
function ExternalPassThrough({ targetPath }) {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const port = window.location.port;
      // In local dev mode (React dev server on port 3001), forward to Express backend on port 3000
      if (port === '3001') {
        window.location.replace(`http://${window.location.hostname}:3000${targetPath}`);
      } else {
        window.location.replace(targetPath);
      }
    }
  }, [targetPath]);

  return null;
}

const App = () => {
  const [isMiniApp, setIsMiniApp] = useState(false);

  // Sync theme color, background color, and card color dynamically from server configuration if set
  useEffect(() => {
    getAuthConfig().then((cfg) => {
      if (cfg && (cfg.themeColor || cfg.bgColor || cfg.cardBgColor)) {
        applyThemeColor(cfg.themeColor, cfg.bgColor, cfg.cardBgColor);
      }
    }).catch(() => {});
  }, []);

  // Initialize Telegram Mini App SDK and handle automatic authentication ONLY if loaded inside Telegram Mini App
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isTg = isTelegramMiniApp();
      setIsMiniApp(isTg);

      if (isTg) {
        document.documentElement.classList.add('is-mini-app');
        document.body.classList.add('is-mini-app');

        // Dynamically load the WebApp SDK from local server
        loadTelegramWebAppScript().then((webApp) => {
          if (!webApp) return;

          try {
            // If user has not yet authenticated, exchange WebApp initData for session
            const initData = webApp.initData;
            const existingToken = localStorage.getItem('token');
            if (initData && !existingToken) {
              fetch('/api/auth/telegram-webapp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ initData })
              })
                .then(res => res.json())
                .then(data => {
                  if (data.success && data.token) {
                    localStorage.setItem('token', data.token);
                    if (data.user) {
                      localStorage.setItem('telegram_user', JSON.stringify(data.user));
                      localStorage.setItem('telegramUser', JSON.stringify(data.user));
                    }
                    window.dispatchEvent(new Event('authChange'));
                    window.dispatchEvent(new Event('domainer_auth_change'));
                    console.log('✅ Auto-authenticated via Telegram WebApp:', data.user?.username || data.user?.id);
                  }
                })
                .catch(err => {
                  console.warn('Telegram WebApp auth note:', err.message);
                });
            }
          } catch (e) {
            console.error('Error initializing Telegram WebApp:', e);
          }
        });
      }
    }
  }, []);

  // 10-minute inactivity auto-logout tracking (applies to website and Telegram Mini App)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
    const ACTIVITY_KEY = 'last_user_activity';

    const updateActivity = () => {
      localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    };

    const logoutDueToInactivity = () => {
      const hasSession = localStorage.getItem('token') || localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
      if (hasSession) {
        localStorage.removeItem('token');
        localStorage.removeItem('telegram_user');
        localStorage.removeItem('telegramUser');
        localStorage.removeItem('adminToken');
        localStorage.removeItem(ACTIVITY_KEY);
        window.dispatchEvent(new Event('authChange'));
        window.dispatchEvent(new Event('domainer_auth_change'));
        console.log('🔒 Session closed automatically due to 10 minutes of inactivity');
      }
    };

    // Initialize activity timestamp
    updateActivity();

    // Event listeners to track any user interaction
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handleUserInteraction = () => {
      updateActivity();
    };

    activityEvents.forEach(evt => {
      window.addEventListener(evt, handleUserInteraction, { passive: true });
    });

    // Check periodically (every 15 seconds) if 10 minutes have elapsed since last activity
    const interval = setInterval(() => {
      const hasSession = localStorage.getItem('token') || localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
      if (!hasSession) return;

      const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
      if (lastActivity && Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS) {
        logoutDueToInactivity();
      }
    }, 15000);

    return () => {
      activityEvents.forEach(evt => {
        window.removeEventListener(evt, handleUserInteraction);
      });
      clearInterval(interval);
    };
  }, []);

  return (
    <Router>
      <Navbar />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/sitemap.xml" element={<ExternalPassThrough targetPath="/sitemap.xml" />} />
        <Route path="/sitemap" element={<ExternalPassThrough targetPath="/sitemap.xml" />} />
        <Route path="/robots.txt" element={<ExternalPassThrough targetPath="/robots.txt" />} />
        <Route path="/whois" element={<DomainSearchPage />} />
        <Route path="/whois.html" element={<DomainSearchPage />} />
        <Route path="/search" element={<Navigate to="/whois" replace />} />
        <Route path="/search.html" element={<Navigate to="/whois" replace />} />
        <Route path="/faq.html" element={<FAQPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/contact.html" element={<ContactPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/contacts.html" element={<ContactPage />} />
        <Route path="/privacy.html" element={<PrivacyPolicyPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms.html" element={<TermsOfServicePage />} />
        <Route path="/parking" element={<Navigate to="/cabinet" replace />} />
        {/* Personal cabinet routes */}
        <Route path="/cabinet" element={<PersonalCabinetPage />} />
        <Route path="/cabinet/:cabinetId" element={<PersonalCabinetPage />} />
        {/* Admin panel route - validation happens in the component */}
        <Route path="/admin/:adminUrl" element={<AdminPanelPage />} />
        {/* Generic domain routes */}
        <Route path="/domain/:domainName" element={<DomainPage />} />
        <Route path="/domain/:domainName.html" element={<DomainPage />} />
        <Route path="/:domainName.html" element={<DomainPage />} />
        {/* Catch-all route for 404 pages */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {isMiniApp && <MiniAppTabBar />}
    </Router>
  );
};

export default App;